<script setup>
import { ref, reactive, onMounted } from 'vue';
import api from '@/services/api';
import Swal from 'sweetalert2';
import logo from "@/assets/img/fondotam-blanco.png";

// Importación de los servicios de reporte independientes
import { ServicioReporte } from '@/js/FormularioServicios';
import { MantenimientoReporte } from '@/js/FormularioMantenimiento';

const loading = ref(true);
const solicitudes = ref([]);
const ticketSeleccionado = ref(null);
const modalAtencion = ref(false);
const usuarioActual = JSON.parse(localStorage.getItem('user')) || { id: 0 };

// Formulario reactivo con switch de tipo de informe
const formIntervencion = reactive({
  tipo_informe: 'SERVICIO', // Decisión del técnico
  diagnostico_previo: '',
  detalles_reparacion: '',
  modalidad: 'Sitio',
  severidad_falla: 2,
  estado_post_intervencion: 'Bueno',
  lectura_uso_equipo: 0,
  verificado_por: '',
  conclusion: 'Equipo operativo',
  recomendacion_tecnica: '',
  // Checklist para Mantenimiento
  control_calidad: {
    so: true, office: true, antivirus: true, sonido: true, video: true, red: true
  }
});

const obtenerTickets = async () => {
  loading.value = true;
  try {
    const res = await api.get('solicitudes/?gestion=true');
    solicitudes.value = res.data.results || res.data;
  } catch (e) {
    console.error("Error al cargar tickets operativos", e);
  } finally {
    loading.value = false;
  }
};

const tomarTicket = async (ticket) => {
  try {
    await api.post(`solicitudes/${ticket.id}/tomar_ticket/`);
    Swal.fire('Asignado', 'Has tomado la responsabilidad del ticket.', 'success');
    obtenerTickets();
  } catch (e) {
    Swal.fire('Error', 'No se pudo asignar el ticket.', 'error');
  }
};

const abrirGestion = (ticket) => {
  ticketSeleccionado.value = ticket;
  // Valores por defecto basados en el hallazgo inicial
  formIntervencion.lectura_uso_equipo = ticket.activo_horas_uso || 0;
  formIntervencion.verificado_por = ticket.solicitante_nombre;
  formIntervencion.diagnostico_previo = '';
  formIntervencion.detalles_reparacion = '';
  modalAtencion.value = true;
};

const ejecutarImpresion = (dataIntervencion) => {
  let html = "";
  if (formIntervencion.tipo_informe === 'MANTENIMIENTO') {
    html = MantenimientoReporte.generarHTML(ticketSeleccionado.value, dataIntervencion);
  } else {
    html = ServicioReporte.generarHTML(ticketSeleccionado.value, dataIntervencion);
  }

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  setTimeout(() => {
    win.print();
    win.close();
  }, 500);
};

const guardarIntervencion = async (finalizar = true) => {
  if (!formIntervencion.diagnostico_previo) {
    return Swal.fire('Atención', 'Debe ingresar el diagnóstico técnico.', 'warning');
  }

  try {
    const payload = {
      solicitud: ticketSeleccionado.value.id,
      activo: ticketSeleccionado.value.activo,
      // Se guarda el tipo que el técnico definió en la gestión
      tipo_mantenimiento: formIntervencion.tipo_informe === 'MANTENIMIENTO' ? 'Correctivo' : 'Servicio',
      modalidad: formIntervencion.modalidad,
      diagnostico_previo: formIntervencion.diagnostico_previo,
      detalles_reparacion: formIntervencion.detalles_reparacion,
      verificado_por: formIntervencion.verificado_por,
      conclusion: formIntervencion.conclusion,
      recomendacion_tecnica: formIntervencion.recomendacion_tecnica,
      lectura_uso_equipo: formIntervencion.lectura_uso_equipo,
      estado_post_intervencion: formIntervencion.estado_post_intervencion,
      fecha_fin: finalizar ? new Date().toISOString() : null
    };

    const res = await api.post('intervenciones/', payload);
    
    if (finalizar) {
      Swal.fire({
        title: '¡Ticket Cerrado!',
        text: '¿Desea imprimir el formulario técnico ahora?',
        icon: 'success',
        showCancelButton: true,
        confirmButtonText: 'Imprimir PDF',
        cancelButtonText: 'Solo finalizar'
      }).then((result) => {
        if (result.isConfirmed) ejecutarImpresion(res.data);
      });
    }

    modalAtencion.value = false;
    obtenerTickets();
  } catch (e) {
    Swal.fire('Error', 'No se pudo registrar la intervención.', 'error');
  }
};

onMounted(obtenerTickets);
</script>

<template>
  <div class="container-fluid py-4 app-transition">
    <div class="card shadow border-radius-xl card-custom overflow-hidden">
      <div class="header-tamep px-4 py-3 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="logo-container-left me-3 p-1 rounded-lg shadow-sm" style="border-radius: 8px; background-color: #ffffff !important;">
            <img :src="logo" class="logo-img p-1" style="max-height: 40px;">
          </div>
          <div class="border-start ps-3 border-white opacity-8">
            <h6 class="text-white font-weight-bolder text-uppercase mb-0">Bandeja de Entrada Técnica</h6>
            <small class="text-white opacity-8">Gestión de incidentes y mantenimiento</small>
          </div>
        </div>
        <button @click="obtenerTickets" class="btn btn-sm mb-0 shadow-sm btn-light font-weight-bolder">
          <i class="fas fa-sync-alt me-1"></i> Actualizar
        </button>
      </div>
      <div class="card-body bg-section-custom px-0 pt-0 pb-2">
        <div class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr class="bg-gray-100">
                <th class="text-xxs font-weight-bolder opacity-7 ps-4">Ticket / Equipo</th>
                <th class="text-xxs font-weight-bolder opacity-7">Asignación</th>
                <th class="text-center text-xxs font-weight-bolder opacity-7">Prioridad</th>
                <th class="text-center text-xxs font-weight-bolder opacity-7">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sol in solicitudes" :key="sol.id">
                <td class="ps-4">
                  <span class="text-xs font-weight-bold d-block">{{ sol.num_ticket }}</span>
                  <span class="text-xxs text-secondary">{{ sol.activo_codigo }}</span>
                </td>
                <td>
                  <span v-if="sol.tecnico_asignado" class="text-xs">
                    <i class="fas fa-user-tie me-1"></i> {{ sol.tecnico_username }}
                  </span>
                  <span v-else class="text-xs text-danger font-weight-bold">
                    <i class="fas fa-unlock me-1"></i> Disponible
                  </span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="sol.severidad === '3' ? 'bg-gradient-danger' : 'bg-gradient-info'">
                    Nivel {{ sol.severidad }}
                  </span>
                </td>
                <td class="text-center">
                  <button v-if="!sol.tecnico_asignado" @click="tomarTicket(sol)" class="btn btn-xs btn-outline-primary mb-0">
                    Tomar
                  </button>
                  <button v-else-if="sol.tecnico_asignado === usuarioActual.id" @click="abrirGestion(sol)" class="btn btn-xs btn-success mb-0">
                    Gestionar
                  </button>
                  <button v-else disabled class="btn btn-xs btn-light mb-0">Ocupado</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="modalAtencion" class="modal-overlay">
      <div class="card modal-card shadow-2xl tamep-modal-content">
        <div class="header-tamep px-4 py-3 d-flex justify-content-between align-items-center border-radius-top">
          <h6 class="text-white font-weight-bolder mb-0"><i class="fas fa-tools me-2"></i>Diagnóstico y Resolución: {{ ticketSeleccionado.num_ticket }}</h6>
        </div>
        <div class="card-body p-4 bg-section-custom rounded-bottom">
          
          <div class="mb-4">
            <label class="text-xxs font-weight-bold text-uppercase">Categorización del Trabajo Realizado</label>
            <div class="d-flex gap-4 mt-1 p-3 bg-white border-radius-lg border">
              <div class="form-check">
                <input class="form-check-input" type="radio" value="SERVICIO" v-model="formIntervencion.tipo_informe" id="repServ">
                <label class="form-check-label text-xs mb-0" for="repServ"><b>Formulario de Servicio</b> (Soporte/Software)</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="radio" value="MANTENIMIENTO" v-model="formIntervencion.tipo_informe" id="repManto">
                <label class="form-check-label text-xs mb-0" for="repManto"><b>Formulario de Mantenimiento</b> (Hardware/Físico)</label>
              </div>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-8">
              <label class="text-xxs font-weight-bold">DIAGNÓSTICO TÉCNICO</label>
              <textarea v-model="formIntervencion.diagnostico_previo" class="form-control text-sm" rows="2" placeholder="Describa el problema encontrado..."></textarea>
            </div>
            <div class="col-md-4">
              <label class="text-xxs font-weight-bold">MODALIDAD</label>
              <select v-model="formIntervencion.modalidad" class="form-select text-xs">
                <option value="Sitio">En Sitio</option>
                <option value="Taller">En Taller</option>
              </select>
            </div>
          </div>

          <div class="mt-3">
            <label class="text-xxs font-weight-bold">TRABAJO REALIZADO</label>
            <textarea v-model="formIntervencion.detalles_reparacion" class="form-control text-sm" rows="3" placeholder="Describa las acciones tomadas..."></textarea>
          </div>

          <div v-if="formIntervencion.tipo_informe === 'MANTENIMIENTO'" class="mt-4 p-3 border-radius-lg bg-white border border-warning animate__animated animate__fadeIn">
            <label class="text-xxs font-weight-bold text-warning mb-2">CONTROL DE CALIDAD (HARDWARE)</label>
            <div class="row text-xxs">
              <div class="col-4" v-for="(val, key) in formIntervencion.control_calidad" :key="key">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="formIntervencion.control_calidad[key]" :id="key">
                  <label class="form-check-label text-uppercase" :for="key">{{ key }}</label>
                </div>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-md-6">
                <label class="text-xxs">RECOMENDACIÓN TÉCNICA</label>
                <input v-model="formIntervencion.recomendacion_tecnica" class="form-control form-control-sm text-xs">
              </div>
              <div class="col-md-6">
                <label class="text-xxs">HORAS DE USO REGISTRADAS</label>
                <input v-model="formIntervencion.lectura_uso_equipo" type="number" class="form-control form-control-sm text-xs">
              </div>
            </div>
          </div>

        </div>
        <div class="card-footer d-flex justify-content-between bg-white">
          <button @click="modalAtencion = false" class="btn btn-sm btn-outline-secondary mb-0">Cerrar</button>
          <div>
            <button @click="guardarIntervencion(false)" class="btn btn-sm btn-warning me-2 mb-0">Guardar Avance</button>
            <button @click="guardarIntervencion(true)" class="btn btn-sm btn-success mb-0">Finalizar y Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-transition { transition: all 0.3s ease; }
.header-tamep { background: linear-gradient(135deg, #003366 0%, #001d3d 100%); }
.border-radius-top { border-top-left-radius: 12px; border-top-right-radius: 12px; }

.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 29, 61, 0.75); z-index: 1000; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); }
.modal-card { width: 850px; max-width: 95vw; border: none; overflow: hidden; }

.card-custom { background-color: #ffffff; transition: background-color 0.3s ease, box-shadow 0.3s ease; }
.bg-section-custom { background-color: #f8f9fa; }
</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom, .dark-mode .card-custom {
  background-color: #1e1e1e !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5) !important;
  border: 1px solid #2d2d2d !important;
}

.dark-version .bg-section-custom, .dark-mode .bg-section-custom {
  background-color: #121212 !important;
}

.dark-version .tamep-modal-content, .dark-mode .tamep-modal-content {
  background-color: #1a1a1a !important;
  border: 1px solid #333 !important;
}

.dark-version .tamep-modal-content .bg-white, .dark-mode .tamep-modal-content .bg-white {
  background-color: #2a2a2a !important;
  border-color: #444 !important;
}

.dark-version .bg-gray-100, .dark-mode .bg-gray-100 {
  background-color: #2a2a2a !important;
  border: 1px solid #333 !important;
}

.dark-version .table, .dark-mode .table {
  color: #e0e0e0 !important;
}

.dark-version .table thead th, .dark-mode .table thead th {
  background-color: #2a2a2a !important;
  color: #ffffff !important;
  border-bottom: 1px solid #444 !important;
}

.dark-version .table td, .dark-mode .table td {
  border-bottom: 1px solid #333 !important;
}

.dark-version .form-control, .dark-mode .form-control {
  background-color: #2a2a2a !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}

.dark-version .form-control:focus, .dark-mode .form-control:focus {
  border-color: #4dabf7 !important;
}

.dark-version .form-select, .dark-mode .form-select {
  background-color: #2a2a2a !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}

.dark-version label, .dark-mode label { color: #aaaaaa !important; }
</style>